import { useState } from "react";
import { DataTable } from "../components/DataTable";
import { StatusBadge } from "../components/StatusBadge";
import { Download } from "lucide-react";
import { useCsvExport } from "../api/exports";
import {
  auditActionLabel,
  auditQuery,
  useAuditActions,
  useAuditLog,
  type AuditEntry,
  type AuditLogParams,
  type AuditSearchField,
} from "../api/audit";
import { useDebouncedValue } from "../hooks/useDebouncedValue";

const PAGE_SIZE_OPTIONS = [25, 50, 100];
const SEARCH_DEBOUNCE_MS = 300;

const searchFields: { value: AuditSearchField; label: string }[] = [
  { value: "entityId", label: "Entity ID" },
  { value: "entityType", label: "Entity type" },
  { value: "userId", label: "User ID" },
];

interface FilterState {
  search: string;
  searchField: AuditSearchField;
  action: string;
}

// One entry per page after the first: its cursor and how many rows came
// before it (pages can be short, so offsets are tracked, not computed).
interface PageMark {
  cursor: string;
  offset: number;
}

export function AuditLogPage() {
  const [filters, setFilters] = useState<FilterState>({ search: "", searchField: "entityId", action: "" });
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [marks, setMarks] = useState<PageMark[]>([]);
  const debouncedSearch = useDebouncedValue(filters.search, SEARCH_DEBOUNCE_MS);
  const currentMark = marks[marks.length - 1];
  const page = marks.length + 1;

  // Every filter is applied by the server across the whole log; the table
  // renders the page it is given.
  const params: AuditLogParams = {
    limit: pageSize,
    cursor: currentMark?.cursor,
    action: filters.action || undefined,
    search: debouncedSearch,
    searchField: filters.searchField,
  };
  const { data, isLoading, error, isPlaceholderData } = useAuditLog(params);
  // The action filter offers exactly what the backend records.
  const actionOptions = (useAuditActions().data?.data ?? []).map((action) => ({
    value: action,
    label: auditActionLabel(action),
  }));

  // Export writes every event matching the filters on screen (not just this
  // page) on the server, then downloads the file.
  const [notice, setNotice] = useState<string | null>(null);
  function showNotice(message: string) {
    setNotice(message);
    setTimeout(() => setNotice(null), 5000);
  }
  const csvExport = useCsvExport("audit", {
    onDone: (job) => showNotice(`Exported ${job.rowCount.toLocaleString()} event${job.rowCount === 1 ? "" : "s"} to CSV.`),
    onError: showNotice,
  });
  const status =
    notice ??
    (csvExport.running
      ? `Preparing CSV export…${csvExport.rowCount > 0 ? ` ${csvExport.rowCount.toLocaleString()} events so far` : ""}`
      : null);

  const entries = data?.data ?? [];
  const hasMore = !!data?.pagination.hasMore;
  const offset = currentMark?.offset ?? 0;
  const rangeLabel = entries.length === 0 ? "" : `${(offset + 1).toLocaleString()}–${(offset + entries.length).toLocaleString()}`;

  function showNextPage() {
    const next = data?.pagination.cursor;
    if (!next || isPlaceholderData) return;
    setMarks([...marks, { cursor: next, offset: offset + entries.length }]);
  }

  const columns = [
    {
      key: "timestamp",
      header: "Timestamp",
      render: (row: AuditEntry) => new Date(row.timestamp).toLocaleString(),
    },
    {
      key: "action",
      header: "Action",
      render: (row: AuditEntry) => <StatusBadge status={row.action} />,
    },
    { key: "entityType", header: "Entity Type" },
    {
      key: "entityId",
      header: "Entity ID",
      render: (row: AuditEntry) => (
        <span className="font-mono text-xs" title={row.entityId}>
          {row.entityId.length > 12 ? `${row.entityId.slice(0, 12)}…` : row.entityId}
        </span>
      ),
    },
    { key: "userId", header: "User" },
    {
      key: "details",
      header: "Details",
      render: (row: AuditEntry) =>
        row.details || <span className="text-gray-400 dark:text-riot-navy-400">-</span>,
    },
  ];

  // A sparse filter can use up the server's per-request budget before it
  // finds a match: an empty page with more to check is not "no results".
  const emptyMessage =
    hasMore && entries.length === 0 && !isPlaceholderData ? (
      <span>
        No matching events in the entries checked so far.{" "}
        <button
          type="button"
          onClick={showNextPage}
          className="font-medium text-riot-gold-600 hover:text-riot-gold-700 dark:text-riot-gold-400"
        >
          Keep searching
        </button>
      </span>
    ) : (
      "No audit events match."
    );

  return (
    <div>
      {error && (
        <p className="mb-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300">
          Failed to load audit log: {String((error as Error).message ?? error)}
        </p>
      )}
      {status && (
        <p
          role="status"
          className="fixed bottom-6 right-6 z-50 rounded-md bg-riot-navy-900 px-4 py-2 text-xs text-white shadow-lg dark:bg-riot-gold-400 dark:text-riot-navy-900"
        >
          {status}
        </p>
      )}
      <DataTable
        title="Audit Log"
        columns={columns}
        data={isLoading ? [] : entries}
        keyExtractor={(e) => e.id}
        serverSide
        emptyMessage={isLoading ? "Loading audit log…" : emptyMessage}
        onFilterChange={(f) => {
          setMarks([]);
          setFilters({
            search: f.search,
            searchField: (f.searchField as AuditSearchField) || "entityId",
            action: f.status,
          });
        }}
        pagination={{
          page,
          count: entries.length,
          hasMore,
          rangeLabel,
          pageSize,
          pageSizeOptions: PAGE_SIZE_OPTIONS,
          onPageSizeChange: (size) => {
            setMarks([]);
            setPageSize(size);
          },
          onNext: showNextPage,
          onPrev: () => setMarks(marks.slice(0, -1)),
          onFirst: () => setMarks([]),
        }}
        filters={{
          searchPlaceholder: "Search events",
          searchFields,
          statusLabel: "Action",
          statusKey: "action",
          statusOptions: actionOptions,
        }}
        actions={
          <button
            type="button"
            disabled={csvExport.running}
            onClick={() => csvExport.start(new URLSearchParams(auditQuery({ ...params, limit: undefined, cursor: undefined })))}
            className="flex items-center gap-1.5 rounded-md border border-gray-300 px-3.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-45 dark:hover:bg-riot-navy-700"
          >
            <Download size={14} /> {csvExport.running ? "Exporting…" : "Export"}
          </button>
        }
      />
    </div>
  );
}
